import { z } from 'zod'
import { guideAnchorPattern } from '../../../shared/routing/guide-target'
import type { Guide } from '../domain/guide'

const text = z
  .string()
  .nullish()
  .transform((value) => value ?? null)
const block = z
  .object({
    type: z.enum(['heading', 'paragraph', 'steps', 'image', 'link']),
    text,
    items: z
      .array(z.string().max(2000))
      .max(20)
      .nullish()
      .transform((value) => value ?? null),
    image_path: text,
    alt: text,
    href: text,
    width: z
      .number()
      .int()
      .min(1)
      .max(20000)
      .nullish()
      .transform((value) => value ?? null),
    height: z
      .number()
      .int()
      .min(1)
      .max(20000)
      .nullish()
      .transform((value) => value ?? null),
    section_id: z
      .uuid()
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((b, ctx) => {
    if (
      b.type === 'image' &&
      (!b.image_path || !b.alt || !b.width || !b.height)
    )
      ctx.addIssue({ code: 'custom', message: 'Invalid guide image geometry' })
    if (
      b.type === 'link' &&
      (!b.text || !(b.section_id ? !b.href : b.href && safeGuideLink(b.href)))
    )
      ctx.addIssue({ code: 'custom', message: 'Invalid guide link' })
  })
  .transform((b) => ({
    type: b.type,
    text: b.text,
    items: b.items,
    imagePath: b.image_path,
    alt: b.alt,
    href: b.href,
    width: b.width,
    height: b.height,
    sectionId: b.section_id,
  }))
export function safeGuideLink(value: string, internalOnly = false): boolean {
  try {
    const decoded = decodeURIComponent(value)
    const url = new URL(value, 'https://cardreplay.invalid')
    return (
      value.length <= 2048 &&
      !/[\p{Cc}\\]/u.test(decoded) &&
      !decoded.startsWith('//') &&
      ((value.startsWith('/') && url.origin === 'https://cardreplay.invalid') ||
        (!internalOnly &&
          url.protocol === 'https:' &&
          !url.username &&
          !url.password))
    )
  } catch {
    return false
  }
}
export const guideTargetSchema = z
  .object({
    kind: z.literal('guide_section'),
    section_id: z.uuid(),
    anchor: z.string().max(120).regex(guideAnchorPattern),
    path: z.string(),
  })
  .refine((v) => v.path === `/guide#${v.anchor}`)
  .transform((v) => ({
    kind: v.kind,
    sectionId: v.section_id,
    anchor: v.anchor,
    path: v.path,
  }))
export const guideStatusSchema = z
  .object({ available: z.boolean(), published_revision_id: z.uuid().nullish() })
  .refine((v) => !v.available || !!v.published_revision_id)
  .transform((v) => ({
    available: v.available,
    revisionId: v.published_revision_id ?? null,
  }))
export const guideSchema = z
  .object({
    schema_version: z.literal(1),
    guide_revision_id: z.uuid(),
    platform: z.enum(['web', 'pwa']),
    groups: z
      .array(
        z.object({
          id: z.uuid(),
          title: z.string().max(140),
          sort_order: z.number().int(),
        }),
      )
      .max(50),
    sections: z
      .array(
        z.object({
          id: z.uuid(),
          group_id: z.uuid(),
          anchor: z.string().max(120).regex(guideAnchorPattern),
          title: z.string().max(140),
          sort_order: z.number().int(),
          blocks: z.array(block).max(100),
          action_label: text,
          action_path: text,
        }),
      )
      .max(500),
    anchor_aliases: z
      .array(
        z.object({
          from_section_id: z.uuid(),
          from_anchor: z.string().max(120).regex(guideAnchorPattern),
          to_section_id: z.uuid(),
        }),
      )
      .max(500),
  })
  .refine(
    (v) =>
      v.sections.every(
        (s) =>
          v.groups.some((g) => g.id === s.group_id) &&
          (!s.action_path || safeGuideLink(s.action_path, true)),
      ) &&
      v.anchor_aliases.every(
        (a) =>
          !v.sections.some((s) => s.id === a.from_section_id) &&
          v.sections.some((s) => s.id === a.to_section_id),
      ),
  )
  .transform((v): Guide => ({
    revisionId: v.guide_revision_id,
    groups: v.groups.map((g) => ({
      id: g.id,
      title: g.title,
      sortOrder: g.sort_order,
    })),
    sections: v.sections.map((s) => ({
      id: s.id,
      groupId: s.group_id,
      anchor: s.anchor,
      title: s.title,
      sortOrder: s.sort_order,
      blocks: s.blocks,
      actionLabel: s.action_label,
      actionPath: s.action_path,
    })),
    aliases: v.anchor_aliases.map((a) => ({
      fromSectionId: a.from_section_id,
      fromAnchor: a.from_anchor,
      toSectionId: a.to_section_id,
    })),
  }))
